/** RCU relay channels are on/off only. Dimmable loads use DIN modules. */

import { ASSIGNMENT_REGISTRY } from '$lib/assignment';
import type { LoadResponse } from '$lib/types/load';

export const DIMMABLE_LOAD_TYPES = ['phase_cut', '1-10v', 'dali-dt6', 'dali-dt8'];

/** DIN rails used for dimmable loads (only these may be added in new designs). */
export const DIN_MODULE_TYPES = {
	phase_cut: {
		key: 'phase_cut',
		label: '4ch Forward Phase Cut',
		channels: 4,
		dimmable: true
	},
	'1-10v': {
		key: '1-10v',
		label: '4ch 0-10V Dimmer',
		channels: 4,
		dimmable: true
	},
	dali_master: {
		key: 'dali_master',
		label: 'DALI Master',
		channelsPerBus: 64,
		dimmable: true
	}
} as const;

/** Legacy DIN types still editable on existing modules. */
export const LEGACY_DIN_MODULE_TYPES = {
	relay: { key: 'relay', label: 'Relay Module (legacy)', channels: 8 },
	curtain: { key: 'curtain', label: 'Curtain Controller (legacy)', channels: 4 }
} as const;

type ChannelRow = {
	source?: string;
	objectId?: number | null;
	moduleType?: string;
	contentType?: number;
};

export function isDimmableLoadType(loadType: string): boolean {
	return DIMMABLE_LOAD_TYPES.includes(loadType);
}

export function isRcuCompatibleLoad(load: Pick<LoadResponse, 'load_type'> | null | undefined): boolean {
	if (!load) return false;
	return (ASSIGNMENT_REGISTRY.rcu.compatible_load_types as readonly string[]).includes(load.load_type);
}

export function dinModuleChannelCount(moduleType: string, daliBusCount = 1): number {
	const spec =
		DIN_MODULE_TYPES[moduleType as keyof typeof DIN_MODULE_TYPES] ??
		LEGACY_DIN_MODULE_TYPES[moduleType as keyof typeof LEGACY_DIN_MODULE_TYPES];
	if (!spec) return 4;
	if (moduleType === 'dali_master' && 'channelsPerBus' in spec) {
		return (spec.channelsPerBus ?? 64) * Math.max(1, daliBusCount ?? 1);
	}
	return 'channels' in spec ? (spec.channels ?? 4) : 4;
}

export function dinModuleLabel(moduleType: string): string {
	const din = DIN_MODULE_TYPES[moduleType as keyof typeof DIN_MODULE_TYPES];
	const legacy = LEGACY_DIN_MODULE_TYPES[moduleType as keyof typeof LEGACY_DIN_MODULE_TYPES];
	return din?.label ?? legacy?.label ?? moduleType;
}

/** Load types compatible with a DIN module (mirrors backend compatibleLoadTypes). */
export function compatibleLoadTypesForDin(moduleType: string): string[] {
	if (moduleType in ASSIGNMENT_REGISTRY && moduleType !== 'rcu') {
		const info = ASSIGNMENT_REGISTRY[moduleType as keyof typeof ASSIGNMENT_REGISTRY];
		return [...info.compatible_load_types];
	}
	return [];
}

export function loadCompatibleWithRow(row: ChannelRow, load: LoadResponse | null | undefined): boolean {
	if (!load) return true;
	if (row.source === 'RCU') {
		return isRcuCompatibleLoad(load);
	}
	if (row.objectId != null && row.source !== 'RCU') {
		const moduleType = row.moduleType;
		if (moduleType) {
			return compatibleLoadTypesForDin(moduleType).includes(load.load_type);
		}
	}
	return true;
}

export function rcuOutputHint(_load: LoadResponse | null | undefined): string {
	return 'On/Off';
}

export function dinOutputHint(load: LoadResponse | null | undefined): string {
	if (!load) return '—';
	if (load.load_type === 'curtain') return 'Open/Close';
	if (isDimmableLoadType(load.load_type)) return 'Dim';
	return 'On/Off';
}

export function channelOutputHint(row: ChannelRow, load: LoadResponse | null | undefined): string {
	if (!load) return '—';
	if (row.source === 'RCU') return rcuOutputHint(load);
	return dinOutputHint(load);
}
